import { Component, OnInit, inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { RouterModule, Router } from '@angular/router';
import { CustomDatePickerComponent } from '../../../../core/shared/components/custom-date-picker/custom-date-picker.component';
import { UsersService } from '../../../../core/services/users.service';
import { AuthService } from '../../../../core/services/auth.service';
import { LoaderService } from '../../../../core/shared/services/loader.service';
import { AppointmentsService } from '../../../../core/services/appointments.service';

interface UserDisplay {
  id: string;
  _id: string;
  name: string;
  avatar?: string;
  status: 'activo' | 'inactivo' | 'pendiente';
  currentStage: string;
  pendingTasks: number;
  nextAppointment: string;
  progress: number;
  createdAt: string;
  email: string;
  firstName: string;
  lastName: string;
}

@Component({
  selector: 'app-users-assigned',
  standalone: true,
  imports: [CommonModule, FormsModule, RouterModule, CustomDatePickerComponent],
  templateUrl: './users-assigned.component.html',
  styleUrl: './users-assigned.component.scss'
})
export class UsersAssignedComponent implements OnInit {
  private authService = inject(AuthService);
  private usersService = inject(UsersService);
  private appointmentsService = inject(AppointmentsService);
  private loader = inject(LoaderService);
  private router = inject(Router);
  
  // Datos reales de usuarios asignados
  users: UserDisplay[] = [];

  // Citas del coach (para calcular próximas citas)
  private coachAppointments: any[] = [];

  // Filter properties
  searchTerm: string = '';
  statusFilter: string = '';
  stageFilter: string = '';
  dateFromFilter: string = '';
  dateToFilter: string = '';

  // Filtered and paginated data
  filteredUsers: UserDisplay[] = [];
  paginatedUsers: UserDisplay[] = [];

  // Pagination properties
  currentPage: number = 1;
  pageSize: number = 10;
  totalPages: number = 1;

  // Sort properties
  sortField: string = 'name';
  sortDirection: 'asc' | 'desc' = 'asc';


  ngOnInit(): void {
    this.loadAssignedUsers();
  }

  loadAssignedUsers(): void {
    this.loader.show();
    const authUser = this.authService.getAuthUser();
    
    if (!authUser) {
      this.loader.hide();
      return;
    }

    this.appointmentsService.findByCoach(authUser.id).subscribe({
      next: (appointmentsResponse) => {
        if (appointmentsResponse && appointmentsResponse.data && Array.isArray(appointmentsResponse.data)) {
          this.coachAppointments = appointmentsResponse.data;
        } else {
          this.coachAppointments = [];
        }
        this.fetchAssignedUsers(authUser.id);
      },
      error: (error) => {
        console.error('Error al cargar citas del coach:', error);
        this.coachAppointments = [];
        this.fetchAssignedUsers(authUser.id);
      }
    });
  }

  private fetchAssignedUsers(coachId: string): void {
    this.usersService.findCustomersByCoach(coachId, this.searchTerm).subscribe({
      next: (response) => {
        if (response && response.data) {
          this.users = this.transformUsersToDisplay(response.data);
          this.applyFilters();
        }
        this.loader.hide();
      },
      error: (error) => {
        console.error('Error al cargar usuarios asignados:', error);
        this.loader.hide();
      }
    });
  }

  private transformUsersToDisplay(users: any[]): UserDisplay[] {
    return users.map(user => ({
      id: user.id || user._id,
      _id: user._id,
      name: `${user.firstName || ''} ${user.lastName || ''}`.trim() || user.name || 'Usuario sin nombre',
      avatar: this.getUserAvatar(user),
      status: this.mapUserStatus(user.status),
      currentStage: this.calculateUserCurrentStage(user),
      pendingTasks: this.calculatePendingTasks(user),
      nextAppointment: this.getNextAppointment(user),
      progress: this.calculateUserProgressPercentage(user),
      createdAt: user.createdAt || new Date().toISOString(),
      email: user.email || '',
      firstName: user.firstName || '',
      lastName: user.lastName || ''
    }));
  }

  private mapUserStatus(status: any): 'activo' | 'inactivo' | 'pendiente' {
    if (!status) return 'pendiente';
    
    const statusName = typeof status === 'string' ? status : status.name;
    switch (statusName?.toLowerCase()) {
      case 'activo':
      case 'active':
        return 'activo';
      case 'inactivo':
      case 'inactive':
        return 'inactivo';
      default:
        return 'pendiente';
    }
  }

  private calculateUserCurrentStage(user: any): string {
    const currentWeek = this.getCurrentWeek(user);
    
    if (currentWeek >= 1 && currentWeek <= 2) {
      return 'Análisis profundo';
    } else if (currentWeek >= 3 && currentWeek <= 3) {
      return 'Mapa de identidad';
    } else if (currentWeek >= 4 && currentWeek <= 4) {
      return 'Phoenix Session';
    } else if (currentWeek >= 5 && currentWeek <= 5) {
      return 'Visionboard';
    } else if (currentWeek >= 6 && currentWeek <= 6) {
      return 'Phoenix Session';
    } else if (currentWeek >= 7 && currentWeek <= 9) {
      return 'PRD 1';
    } else if (currentWeek >= 10 && currentWeek <= 11) {
      return 'Plan de vida';
    } else if (currentWeek >= 12 && currentWeek <= 12) {
      return 'Graduación';
    } else if (currentWeek > 12) {
      return 'Programa completado';
    } else {
      return 'Iniciando programa';
    }
  }

  private calculatePendingTasks(user: any): number {
    // Lógica para calcular tareas pendientes
    let pendingTasks = 0;
    
    if (!user.profile?.registerQuestionsComplete) pendingTasks++;
    if (!user.profile?.testNowComplete) pendingTasks++;
    if (!user.profile?.testFutureComplete) pendingTasks++;
    if (!user.profile?.onboardingComplete) pendingTasks++;
    
    return pendingTasks;
  }

  private getNextAppointment(user: any): string {
    if (!user || !user._id) {
      return 'No programada';
    }

    if (!Array.isArray(this.coachAppointments) || this.coachAppointments.length === 0) {
      return 'No programada';
    }

    const now = new Date();
    const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());

    const futureAppointments = this.coachAppointments.filter(appointment => {
      const customerId =
        appointment.customer?._id ||
        appointment.customer?.id ||
        appointment.customerId ||
        appointment.customer;

      const normalizedCustomerId = customerId ? customerId.toString() : '';
      const matchesId = normalizedCustomerId === user._id;
      if (!matchesId) {
        return false;
      }

      if (!appointment.date) {
        return false;
      } 
      console.log('appointment.date', appointment.date);
      console.log('appointment.time', appointment.time);

      const appointmentDate = this.parseDateString(appointment.date);
      const dateWithTime = this.applyTimeToDate(appointmentDate, appointment.time || appointment.customer?.time);
      console.log('dateWithTime', dateWithTime);
      console.log('now', now);
      console.log('dateWithTime >= now', dateWithTime >= now);
      return dateWithTime >= now;
    });

    if (futureAppointments.length === 0) {
      return 'No programada';
    }

    const nextAppointment = futureAppointments.sort((a, b) =>
      new Date(a.date).getTime() - new Date(b.date).getTime()
    )[0];

    const appointmentDate = new Date(nextAppointment.date);
    const formattedDate = appointmentDate.toLocaleDateString('es-ES', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric'
    });

    let timeDisplay: string = nextAppointment.time;
    if (timeDisplay && !timeDisplay.includes('AM') && !timeDisplay.includes('PM')) {
      timeDisplay = this.convertTo12Hour(timeDisplay);
    }

    return timeDisplay ? `${formattedDate} ${timeDisplay}` : formattedDate;
  }

  private applyTimeToDate(date: Date, time?: string): Date {
    const result = new Date(date.getFullYear(), date.getMonth(), date.getDate());

    if (!time || typeof time !== 'string') {
      return result;
    }

    const trimmed = time.trim();

    if (trimmed.includes('AM') || trimmed.includes('PM')) {
      const match = trimmed.match(/(\d{1,2}):(\d{2})\s*(AM|PM)/i);
      if (!match) {
        return result;
      }

      let hours = parseInt(match[1], 10);
      const minutes = parseInt(match[2], 10);
      const period = match[3].toUpperCase();

      if (period === 'AM') {
        if (hours === 12) hours = 0;
      } else if (period === 'PM') {
        if (hours !== 12) hours += 12;
      }

      result.setHours(hours, minutes, 0, 0);
      return result;
    }

    if (/^\d{1,2}:\d{2}$/.test(trimmed)) {
      const [hoursPart, minutesPart] = trimmed.split(':');
      const hours = parseInt(hoursPart, 10);
      const minutes = parseInt(minutesPart, 10);

      result.setHours(hours, minutes, 0, 0);
      return result;
    }

    const parsed = new Date(`${date.toISOString().split('T')[0]}T${trimmed}`);
    if (!isNaN(parsed.getTime())) {
      result.setHours(parsed.getHours(), parsed.getMinutes(), parsed.getSeconds(), parsed.getMilliseconds());
    }

    return result;
  }

  private parseDateString(dateStr: string): Date {
    if (!dateStr) {
      return new Date();
    }

    const trimmed = dateStr.trim();

    if (trimmed.includes('T')) {
      const parsed = new Date(trimmed);
      if (!isNaN(parsed.getTime())) {
        return parsed;
      }
    }

    const matches = trimmed.match(/^(\d{4})-(\d{2})-(\d{2})$/);
    if (matches) {
      const year = parseInt(matches[1], 10);
      const month = parseInt(matches[2], 10);
      const day = parseInt(matches[3], 10);
      return new Date(year, month - 1, day);
    }

    const parsed = new Date(trimmed);
    if (!isNaN(parsed.getTime())) {
      return parsed;
    }

    return new Date();
  }

  private calculateUserProgressPercentage(user: any): number {
    const currentWeek = this.getCurrentWeek(user);
    
    // El programa tiene 12 semanas, cada semana representa aproximadamente 8.33% (100/12)
    const progressPerWeek = 100 / 12;
    const calculatedProgress = Math.min(currentWeek * progressPerWeek, 100);
    
    return Math.round(calculatedProgress);
  }

  private convertTo12Hour(time24h: string): string {
    if (!time24h) return '';

    const [hoursPart, minutesPart = '00'] = time24h.split(':');
    let hour = parseInt(hoursPart, 10);

    if (isNaN(hour)) {
      return time24h;
    }

    const period = hour >= 12 ? 'PM' : 'AM';
    hour = hour % 12;
    if (hour === 0) {
      hour = 12;
    }

    return `${hour.toString().padStart(2, '0')}:${minutesPart.padStart(2, '0')} ${period}`;
  }

  private getCurrentWeek(user: any): number {
    if (!user?.createdAt) {
      return 1; // Retornar semana 1 por defecto
    }

    const createdAt = new Date(user.createdAt);
    const now = new Date();
    
    // Calcular la diferencia en milisegundos
    const diffInMs = now.getTime() - createdAt.getTime();
    
    // Convertir a días
    const diffInDays = Math.floor(diffInMs / (1000 * 60 * 60 * 24));
    
    // Calcular la semana (redondeando hacia arriba)
    const week = Math.ceil((diffInDays + 1) / 7);
    
    return week;
  }

  private getUserAvatar(user: any): string {
    // Verificar si hay una imagen base64 en personalSection
    if (user.personalSection?.photoBase64) {
      return this.formatImageUrl(user.personalSection.photoBase64);
    }
    
    // Verificar si hay una imagen base64 en la propiedad photo
    if (user.photo) {
      return this.formatImageUrl(user.photo);
    }
    
    // Si no hay imagen base64, retornar el avatar por defecto
    return '/assets/icons/default-avatar.png';
  }

  private formatImageUrl(imageString: string): string {
    // Si ya tiene el prefijo data:image, lo retornamos tal como está
    if (imageString.startsWith('data:image/')) {
      return imageString;
    }
    
    // Si es una URL normal (http/https), lo retornamos tal como está
    if (imageString.startsWith('http://') || imageString.startsWith('https://')) {
      return imageString;
    }
    
    // Si es una ruta de archivo (empieza con /), lo retornamos tal como está
    if (imageString.startsWith('/')) {
      return imageString;
    }
    
    // Si es un string base64 puro, agregamos el prefijo
    if (this.isBase64String(imageString)) {
      return `data:image/jpeg;base64,${imageString}`;
    }
    
    // Si no podemos determinar el tipo, asumimos que es base64
    return `data:image/jpeg;base64,${imageString}`;
  }

  private isBase64String(str: string): boolean {
    try {
      // Verificar que el string solo contenga caracteres válidos para base64
      const base64Regex = /^[A-Za-z0-9+/]*={0,2}$/;
      if (!base64Regex.test(str)) {
        return false;
      }
      
      // Verificar que el string tenga una longitud múltiplo de 4
      if (str.length % 4 !== 0) {
        return false;
      }
      
      // Intentar decodificar el string base64
      const decoded = atob(str);
      
      // Verificar que los primeros bytes correspondan a una imagen
      const bytes = new Uint8Array(decoded.length);
      for (let i = 0; i < decoded.length; i++) {
        bytes[i] = decoded.charCodeAt(i);
      }
      
      // Verificar magic numbers de imágenes comunes
      const isJpeg = bytes[0] === 0xFF && bytes[1] === 0xD8 && bytes[2] === 0xFF;
      const isPng = bytes[0] === 0x89 && bytes[1] === 0x50 && bytes[2] === 0x4E && bytes[3] === 0x47;
      const isGif = bytes[0] === 0x47 && bytes[1] === 0x49 && bytes[2] === 0x46;
      
      return isJpeg || isPng || isGif;
    } catch (error) {
      // Si hay error al decodificar, no es base64 válido
      return false;
    }
  }

  // Filter methods
  onSearchChange(): void {
    this.applyFilters();
  }

  onFilterChange(): void {
    this.applyFilters();
  }

  applyFilters(): void {
    let filtered = [...this.users];

    // Search filter
    if (this.searchTerm) {
      filtered = filtered.filter(user => 
        user.name.toLowerCase().includes(this.searchTerm.toLowerCase())
      );
    }

    // Status filter
    if (this.statusFilter) {
      filtered = filtered.filter(user => user.status === this.statusFilter);
    }

    // Stage filter
    if (this.stageFilter) {
      filtered = filtered.filter(user => 
        user.currentStage.toLowerCase().replace(/\s+/g, '-') === this.stageFilter
      );
    }

    // Date filters (simplified for demo)
    if (this.dateFromFilter) {
      // In a real app, you would compare actual dates
      // For demo purposes, we'll just show all results
    }

    if (this.dateToFilter) {
      // In a real app, you would compare actual dates
      // For demo purposes, we'll just show all results
    }

    this.filteredUsers = filtered;
    this.currentPage = 1; // Reset to first page when filtering
    this.updatePagination();
  }

  // Sort methods
  sortBy(field: string): void {
    if (this.sortField === field) {
      this.sortDirection = this.sortDirection === 'asc' ? 'desc' : 'asc';
    } else {
      this.sortField = field;
      this.sortDirection = 'asc';
    }

    this.filteredUsers.sort((a, b) => {
      let aValue = a[field as keyof UserDisplay];
      let bValue = b[field as keyof UserDisplay];

      // Handle undefined values
      if (aValue === undefined && bValue === undefined) {
        return 0;
      }
      if (aValue === undefined) {
        return this.sortDirection === 'asc' ? 1 : -1;
      }
      if (bValue === undefined) {
        return this.sortDirection === 'asc' ? -1 : 1;
      }

      if (typeof aValue === 'string' && typeof bValue === 'string') {
        aValue = aValue.toLowerCase();
        bValue = bValue.toLowerCase();
      }

      if (aValue < bValue) {
        return this.sortDirection === 'asc' ? -1 : 1;
      }
      if (aValue > bValue) {
        return this.sortDirection === 'asc' ? 1 : -1;
      }
      return 0;
    });

    this.updatePagination();
  }

  // Pagination methods
  updatePagination(): void {
    this.totalPages = Math.ceil(this.filteredUsers.length / this.pageSize);
    
    const startIndex = (this.currentPage - 1) * this.pageSize;
    const endIndex = startIndex + this.pageSize;
    
    this.paginatedUsers = this.filteredUsers.slice(startIndex, endIndex);
  }

  goToPage(page: number): void {
    if (page >= 1 && page <= this.totalPages) {
      this.currentPage = page;
      this.updatePagination();
    }
  }

  getPageNumbers(): number[] {
    const pages: number[] = [];
    const maxVisiblePages = 5;
    
    let startPage = Math.max(1, this.currentPage - Math.floor(maxVisiblePages / 2));
    let endPage = Math.min(this.totalPages, startPage + maxVisiblePages - 1);
    
    if (endPage - startPage + 1 < maxVisiblePages) {
      startPage = Math.max(1, endPage - maxVisiblePages + 1);
    }
    
    for (let i = startPage; i <= endPage; i++) {
      pages.push(i);
    }
    
    return pages;
  }

  // Action methods
  viewUser(user: UserDisplay): void {
    // Navigate to user detail page
    this.router.navigate(['/dashboard/users-assigned', user._id]);
  }

  // Utility methods
  get Math() {
    return Math;
  }
}
